import { QueryClient } from "@tanstack/react-query";

import { isNetworkError } from "../utils/errors";

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

// One more attempt when the API never answered; a status it did answer
// with will not change on retry, and a validation failure is deterministic.
function retry(failureCount: number, error: Error): boolean {
  return failureCount < 1 && isNetworkError(error);
}

/** A page's own entity: an error with nothing to show reaches the route
 *  ErrorBoundary; a failed refresh behind existing data stays quiet. */
export function throwWhenNoData(
  _error: unknown,
  query: { state: { data: unknown } },
): boolean {
  return query.state.data === undefined;
}

export type ThrowOnError = typeof throwWhenNoData;

export function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // A day each, by design: docs/caching.md.
        staleTime: ONE_DAY_MS,
        gcTime: ONE_DAY_MS,
        retry,
        refetchOnWindowFocus: false, // Static content doesn't need refetch
      },
    },
  });
}

let browserQueryClient: QueryClient | undefined;

/**
 * The browser's one QueryClient, created lazily so importing this module on
 * the server is harmless while *using* it there fails loudly — the server's
 * cache is per-request (see server/app.js), and a shared one would leak data
 * across visitors.
 */
export function getBrowserQueryClient(): QueryClient {
  if (typeof document === "undefined") {
    throw new Error(
      "getBrowserQueryClient() on the server — use the per-request client from AppLoadContext",
    );
  }
  return (browserQueryClient ??= makeQueryClient());
}
